import { useState } from 'react';
import axios from 'axios';
import { useMobile } from '../components/Layout';
import { useTheme } from '../ThemeContext';
import { ShieldCheck, ShieldAlert, ShieldX, Download, Search, Wrench, CheckCircle, XCircle } from 'lucide-react';

const API = axios.create({ baseURL: process.env.REACT_APP_API_URL || 'http://localhost:3000' });
API.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const getUserRole = () => {
  try {
    const token = localStorage.getItem('token');
    if (!token) return '';
    return JSON.parse(atob(token.split('.')[1])).role || '';
  } catch { return ''; }
};

const HashChain = () => {
  const isMobile  = useMobile();
  const { theme } = useTheme();
  const isAdmin   = getUserRole() === 'ADMIN';

  const [result,        setResult]        = useState(null);
  const [loading,       setLoading]       = useState(false);
  const [error,         setError]         = useState(null);
  const [chainStatus,   setChainStatus]   = useState(null);
  const [statusLoading, setStatusLoading] = useState(false);
  const [statusError,   setStatusError]   = useState(null);
  const [showConfirm,   setShowConfirm]   = useState(false);
  const [repairing,     setRepairing]     = useState(false);
  const [repairResult,  setRepairResult]  = useState(null);
  const [repairError,   setRepairError]   = useState(null);

  const runVerification = async () => {
    setLoading(true); setError(null); setResult(null);
    try {
      const res = await API.get('/dora/verify-audit-chain');
      setResult(res.data);
    } catch (err) { setError(err.response?.data?.error || 'Verification failed'); }
    finally { setLoading(false); }
  };

  const checkChainStatus = async () => {
    setStatusLoading(true); setStatusError(null); setChainStatus(null);
    setRepairResult(null); setRepairError(null); setShowConfirm(false);
    try {
      const res = await API.get('/dora/admin/chain-status');
      setChainStatus(res.data);
    } catch (err) { setStatusError(err.response?.data?.error || 'Status check failed'); }
    finally { setStatusLoading(false); }
  };

  const runRepair = async () => {
    setRepairing(true); setRepairError(null); setShowConfirm(false);
    try {
      const res = await API.post('/dora/admin/chain-repair');
      setRepairResult(res.data);
      setChainStatus(null); setResult(null);
    } catch (err) { setRepairError(err.response?.data?.error || 'Repair failed'); }
    finally { setRepairing(false); }
  };

  const exportPdf = () => {
    const token = localStorage.getItem('token');
    const url = `${process.env.REACT_APP_API_URL || 'http://localhost:3000'}/dora/verify-audit-chain/pdf`;
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob())
      .then(blob => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `regshield-hash-chain-${new Date().toISOString().slice(0,10)}.pdf`;
        a.click();
      });
  };

  const intactPct = result
    ? Math.round(((result.total_records - result.tampered_records) / result.total_records) * 100)
    : null;

  const allTampersDocumented = result && result.tampered_records > 0 &&
    !result.results.filter(r => !r.hash_valid).some(
      tampered => !result.results.some(r => r.action === 'AUDIT_CHAIN_REMEDIATION_NOTE' && r.hash_valid)
    );

  const chainState = result
    ? result.chain_intact ? 'intact'
    : allTampersDocumented ? 'documented'
    : 'tampered'
    : null;

  const chainMeta = {
    intact:     { bg: '#F0FDF4', border: '#BBF7D0', text: '#166534', icon: ShieldCheck, label: 'Chain Intact — No Tampering Detected' },
    documented: { bg: '#FFFBEB', border: '#FDE68A', text: '#92400E', icon: ShieldAlert, label: 'Historical Anomaly — Documented & Remediated' },
    tampered:   { bg: '#FEF2F2', border: '#FECACA', text: '#991B1B', icon: ShieldX,    label: 'Tampering Detected — Immediate Action Required' },
  };

  const btnStyle = (bg, color, border) => ({
    padding: '9px 18px', borderRadius: 8, border: border || 'none',
    background: bg, color, fontSize: 13, fontWeight: 700, cursor: 'pointer',
    display: 'flex', alignItems: 'center', gap: 6,
  });

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: theme.textPrimary }}>
          Hash Chain Verification
        </h1>
        <p style={{ margin: '4px 0 0', fontSize: 13, color: theme.textSecondary }}>
          Cryptographic verification that the audit log has not been tampered with
        </p>
      </div>

      {/* Explainer card */}
      <div style={{ background: theme.bgCard, borderRadius: 10, padding: '20px 24px', marginBottom: 16, border: `1px solid ${theme.border}`, boxShadow: theme.shadowSm }}>
        <p style={{ margin: '0 0 6px', fontSize: 14, fontWeight: 700, color: theme.textPrimary }}>How it works</p>
        <p style={{ margin: '0 0 20px', fontSize: 13, color: theme.textSecondary, lineHeight: 1.6 }}>
          Every audit record contains a SHA-256 hash of itself plus the previous record.
          This check replays every record and recomputes its hash — any mismatch means
          a record was altered or deleted after it was written.
        </p>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <button onClick={runVerification} disabled={loading} style={btnStyle(loading ? '#BFDBFE' : '#3B82F6', 'white')}>
            <Search size={14} />
            {loading ? 'Verifying…' : 'Run Verification'}
          </button>

          <button onClick={exportPdf} style={btnStyle(theme.bgBadge, theme.textSecondary, `1px solid ${theme.border}`)}>
            <Download size={14} /> Export PDF
          </button>

          {isAdmin && (
            <button onClick={checkChainStatus} disabled={statusLoading || repairing}
              style={btnStyle(statusLoading ? '#FFFBEB' : '#FEF3C7', '#92400E', '1px solid #FDE68A')}>
              <Search size={14} />
              {statusLoading ? 'Checking…' : 'Check Broken Records'}
            </button>
          )}
        </div>
      </div>

      {/* Status errors */}
      {statusError && (
        <div style={{ background: '#FEF2F2', borderRadius: 10, padding: '14px 18px', marginBottom: 16, border: '1px solid #FECACA' }}>
          <p style={{ color: '#DC2626', fontWeight: 600, margin: 0, fontSize: 13 }}>{statusError}</p>
        </div>
      )}

      {/* Repair success */}
      {repairResult && (
        <div style={{ background: '#F0FDF4', borderRadius: 10, border: '1px solid #BBF7D0', padding: '16px 20px', marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            <CheckCircle size={16} color="#166534" />
            <p style={{ color: '#166534', fontWeight: 700, margin: 0, fontSize: 14 }}>Repair Complete</p>
          </div>
          <p style={{ color: '#166534', margin: 0, fontSize: 13 }}>
            {repairResult.message} Run verification to confirm the chain is now intact.
          </p>
        </div>
      )}

      {/* Repair error */}
      {repairError && (
        <div style={{ background: '#FEF2F2', borderRadius: 10, padding: '14px 18px', marginBottom: 16, border: '1px solid #FECACA' }}>
          <p style={{ color: '#DC2626', fontWeight: 600, margin: 0, fontSize: 13 }}>Repair failed: {repairError}</p>
          <p style={{ color: '#DC2626', margin: '4px 0 0', fontSize: 12 }}>The no_update_audit rule was not modified — transaction rolled back automatically.</p>
        </div>
      )}

      {/* Chain status (ADMIN) */}
      {chainStatus && !repairResult && (
        <div style={{
          background: chainStatus.chain_intact ? '#F0FDF4' : '#FFFBEB',
          border: `1px solid ${chainStatus.chain_intact ? '#BBF7D0' : '#FDE68A'}`,
          borderRadius: 10, padding: '18px 22px', marginBottom: 16,
        }}>
          {chainStatus.chain_intact ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <CheckCircle size={16} color="#166534" />
              <p style={{ color: '#166534', fontWeight: 700, margin: 0, fontSize: 14 }}>Chain is intact — no broken records found.</p>
            </div>
          ) : (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <ShieldAlert size={16} color="#92400E" />
                <p style={{ color: '#92400E', fontWeight: 700, margin: 0, fontSize: 14 }}>
                  {chainStatus.total_broken} broken record{chainStatus.total_broken !== 1 ? 's' : ''} detected
                </p>
              </div>
              <p style={{ color: '#92400E', margin: '0 0 16px', fontSize: 13, lineHeight: 1.6 }}>
                The hash chain can be repaired by recomputing the correct hashes. The no_update_audit rule
                will be dropped for the duration and recreated immediately after.
              </p>

              {showConfirm ? (
                <div style={{ background: theme.bgCard, border: `1px solid #FDE68A`, borderRadius: 8, padding: '16px 18px' }}>
                  <p style={{ margin: '0 0 8px', fontSize: 13, fontWeight: 600, color: theme.textPrimary }}>
                    Confirm repair of {chainStatus.total_broken} record{chainStatus.total_broken !== 1 ? 's' : ''}?
                  </p>
                  <p style={{ margin: '0 0 16px', fontSize: 12, color: theme.textSecondary, lineHeight: 1.5 }}>
                    This will drop the no_update_audit rule, recompute all broken chain hashes, recreate the rule,
                    and write one CHAIN_VERIFICATION audit entry. This action cannot be undone.
                  </p>
                  <div style={{ display: 'flex', gap: 10 }}>
                    <button onClick={runRepair} disabled={repairing}
                      style={btnStyle(repairing ? '#FCA5A5' : '#DC2626', 'white')}>
                      <Wrench size={13} />
                      {repairing ? 'Repairing…' : 'Confirm Repair'}
                    </button>
                    <button onClick={() => setShowConfirm(false)} disabled={repairing}
                      style={btnStyle(theme.bgBadge, theme.textSecondary, `1px solid ${theme.border}`)}>
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <button onClick={() => setShowConfirm(true)}
                  style={btnStyle('#92400E', 'white')}>
                  <Wrench size={13} /> Repair Chain
                </button>
              )}
            </>
          )}
        </div>
      )}

      {/* Verification error */}
      {error && (
        <div style={{ background: '#FEF2F2', borderRadius: 10, padding: '14px 18px', marginBottom: 16, border: '1px solid #FECACA' }}>
          <p style={{ color: '#DC2626', fontWeight: 600, margin: 0, fontSize: 13 }}>{error}</p>
        </div>
      )}

      {/* Verification result */}
      {result && (() => {
        const meta = chainMeta[chainState];
        const Icon = meta.icon;
        return (
          <>
            {/* Summary */}
            <div style={{ background: meta.bg, border: `1px solid ${meta.border}`, borderRadius: 12, padding: isMobile ? '18px' : '24px 28px', marginBottom: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                <Icon size={20} color={meta.text} />
                <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: meta.text }}>{meta.label}</h3>
              </div>
              {chainState === 'documented' && (
                <p style={{ margin: '-8px 0 16px', fontSize: 12, color: meta.text, lineHeight: 1.5 }}>
                  {result.tampered_records} record{result.tampered_records > 1 ? 's were' : ' was'} written during
                  initial development using raw SQL. This has been documented with a remediation note.
                  The chain is intact from that point forward. No action required.
                </p>
              )}
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(5, 1fr)', gap: isMobile ? 12 : 0 }}>
                {[
                  { label: 'Total Records', value: result.total_records, color: theme.textPrimary },
                  { label: 'Valid',          value: result.total_records - result.tampered_records, color: '#166534' },
                  { label: 'Tampered',       value: result.tampered_records, color: result.tampered_records > 0 ? '#DC2626' : '#166534' },
                  { label: 'Integrity',      value: `${intactPct}%`, color: intactPct === 100 ? '#166534' : '#DC2626' },
                  { label: 'Verified At',    value: new Date(result.verified_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }), color: theme.textPrimary },
                ].map(s => (
                  <div key={s.label} style={{ textAlign: 'center' }}>
                    <p style={{ margin: 0, fontSize: 9, color: meta.text, fontWeight: 700, letterSpacing: '0.06em', opacity: 0.7, textTransform: 'uppercase' }}>{s.label}</p>
                    <p style={{ margin: '4px 0 0', fontSize: isMobile ? 18 : 22, fontWeight: 800, color: s.color }}>{s.value}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Record details */}
            <div style={{ background: theme.bgCard, borderRadius: 10, border: `1px solid ${theme.border}`, overflow: 'hidden', boxShadow: theme.shadowSm }}>
              <div style={{ padding: '14px 18px', borderBottom: `1px solid ${theme.border}` }}>
                <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: theme.textPrimary }}>Record Details</h3>
              </div>

              {isMobile ? (
                <div style={{ padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {result.results.map(r => (
                    <div key={r.record_id} style={{
                      borderRadius: 8, padding: '12px 14px',
                      background: r.hash_valid ? theme.bgBase : '#FEF2F2',
                      border: `1px solid ${r.hash_valid ? theme.border : '#FECACA'}`,
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                        <span style={{ background: r.hash_valid ? '#F0FDF4' : '#FEF2F2', color: r.hash_valid ? '#166534' : '#991B1B', padding: '2px 9px', borderRadius: 99, fontWeight: 700, fontSize: 11 }}>
                          {r.hash_valid ? '✓ Valid' : '✗ Tampered'}
                        </span>
                        <span style={{ fontSize: 11, color: theme.textTertiary }}>
                          {new Date(r.timestamp).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </div>
                      <p style={{ margin: 0, fontSize: 12, fontWeight: 600, color: theme.textPrimary }}>{r.action.replace(/_/g, ' ')}</p>
                      {r.entity_id && <p style={{ margin: '2px 0 0', fontSize: 11, color: theme.textSecondary }}>{r.entity_id}</p>}
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                    <thead>
                      <tr style={{ background: theme.bgBase, borderBottom: `1px solid ${theme.border}` }}>
                        {['Status', 'Timestamp', 'Action', 'Entity', 'Stored Hash', 'Expected Hash'].map(h => (
                          <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontSize: 10, fontWeight: 700, color: theme.textTertiary, letterSpacing: '0.07em', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {result.results.map((r, idx) => (
                        <tr key={r.record_id} style={{ borderBottom: idx < result.results.length - 1 ? `1px solid ${theme.borderLight}` : 'none', background: r.hash_valid ? 'transparent' : '#FEF2F215', transition: 'background 0.1s' }}
                          onMouseEnter={e => e.currentTarget.style.background = r.hash_valid ? theme.bgCardHover : '#FEF2F230'}
                          onMouseLeave={e => e.currentTarget.style.background = r.hash_valid ? 'transparent' : '#FEF2F215'}
                        >
                          <td style={{ padding: '10px 14px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                              {r.hash_valid
                                ? <CheckCircle size={13} color="#166534" />
                                : <XCircle size={13} color="#DC2626" />}
                              <span style={{ fontSize: 11, fontWeight: 700, color: r.hash_valid ? '#166834' : '#DC2626' }}>
                                {r.hash_valid ? 'Valid' : 'Tampered'}
                              </span>
                            </div>
                          </td>
                          <td style={{ padding: '10px 14px', color: theme.textSecondary, whiteSpace: 'nowrap' }}>
                            {new Date(r.timestamp).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                          </td>
                          <td style={{ padding: '10px 14px', color: theme.textPrimary, fontWeight: 500 }}>
                            {r.action.replace(/_/g, ' ')}
                          </td>
                          <td style={{ padding: '10px 14px', color: theme.textSecondary, maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {r.entity_id || '—'}
                          </td>
                          <td style={{ padding: '10px 14px', color: theme.textTertiary, fontFamily: 'monospace', maxWidth: 100, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {r.stored_hash?.substring(0, 16)}…
                          </td>
                          <td style={{ padding: '10px 14px', color: theme.textTertiary, fontFamily: 'monospace', maxWidth: 100, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {r.expected_hash?.substring(0, 16)}…
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        );
      })()}
    </div>
  );
};

export default HashChain;